import {isUndefined, omitBy} from 'lodash-es';
import moment from 'moment';

import {IContextData} from 'modules/context/context-types';
import {DATE_FOR_REQUEST} from 'shared/constants/date-format';
import {isEmptyArrayOrObject} from 'shared/utils';
import {IUserInfo} from 'store/slices/auth-slice/auth-slice-types';
import {TReportConfigurationQueryParameterDefaultType, IReportConfigurationQueryParameterDto, TReportConfigurationQueryParameterVariableType} from 'store/slices/report-configuration-slice/report-configuration-dto';

import {ETableReportFilterFieldDefaultType, TTableReportQueryParameters} from '../table-report-slice-types';

/**
 * Проверяет, является ли значение "пустым" (undefined, пустой массив/объект или пустая строка)
 * Примечание: специально возвращает false для null, так как null считается значимым значением
 *
 * @param {any} value - Проверяемое значение
 * @returns {boolean} - true, если значение считается "пустым"
 *
 * @example
 * isEmptyIgnoreNull(undefined); // true
 * isEmptyIgnoreNull([]); // true
 * isEmptyIgnoreNull({}); // true
 * isEmptyIgnoreNull(''); // true
 * isEmptyIgnoreNull(null); // false
 * isEmptyIgnoreNull(0); // false
 * isEmptyIgnoreNull(false); // false
 */
const isEmptyIgnoreNull = (value: any): boolean => {
    if (value === null) return false;
    return isEmptyArrayOrObject(value);
};

/**
 * Удаляет из объекта поля с "пустыми" значениями (undefined, пустые массивы/объекты, пустые строки)
 * Специально не удаляет поля со значением null, так как null считается значимым значением
 *
 * @template T - Тип исходного объекта
 * @param {T} [values] - Объект для обработки
 * @returns {T | undefined} - Новый объект без пустых полей или undefined, если передан undefined
 *
 * @example
 * omitEmptyParameterFields({
 *   name: 'John',
 *   age: null,
 *   tags: [],
 *   address: {},
 *   email: ''
 * }); // Возвращает: { name: 'John', age: null }
 */
export const omitEmptyParameterFields = (values?: { [key: string]: any }) => {
    if (!values) return undefined;

    return omitBy(
        omitBy(
            omitBy(values, isUndefined), // Удаляем только undefined
            isEmptyIgnoreNull, // Удаляем пустые массивы и объекты, но не null
        ),
        v => v === '', // Удаляем пустые строки
    ) as typeof values | undefined;
};

export interface IGetTableReportFilterDefaultValueArgs {
    type: TReportConfigurationQueryParameterVariableType;
    defaultType: TReportConfigurationQueryParameterDefaultType;
    context: IContextData | undefined;
    userInfo: IUserInfo | undefined;
    defaultValue: unknown;
}

export const getTableReportFilterDefaultValue = (
    {
        context,
        userInfo,
        defaultType,
        defaultValue,
    }: IGetTableReportFilterDefaultValueArgs,
) => {
    switch (defaultType) {
    case ETableReportFilterFieldDefaultType.CURRENT_DATETIME:
        return moment(new Date()).format(DATE_FOR_REQUEST);
    case ETableReportFilterFieldDefaultType.CONTEXT:
        if (defaultValue === 'TAX_PERIOD_ID') {
            return context?.taxPeriodId?.id;
        }
        if (defaultValue === 'ORG_ID') {
            return context?.organizationId?.id;
        }
        if (defaultValue === 'USER_ID') {
            return userInfo?.userId;
        }
        if (defaultValue === 'USER_IS_INSPECTOR') {
            return !!userInfo?.externalEmployee;
        }
        return defaultValue;
    default:
        return defaultValue;
    }
};

export interface ICreateFilterDefaultValuesArgs {
    queryParameters: IReportConfigurationQueryParameterDto[] | undefined;
    filterKeyPath: 'paramName' | 'columnName';
    context: IContextData;
    userInfo: IUserInfo | undefined;
}

export const createFilterDefaultValues = ({
    context,
    filterKeyPath,
    queryParameters,
    userInfo,
}: ICreateFilterDefaultValuesArgs) => queryParameters?.reduce((acc, queryParameter) => {
    const {type, defaultType, defaultValue} = queryParameter;
    const queryParameterName = queryParameter[filterKeyPath];

    return {
        ...acc,
        [queryParameterName]: getTableReportFilterDefaultValue({
            context,
            userInfo,
            defaultType,
            defaultValue,
            type,
        }),
    };
}, {} as TTableReportQueryParameters);
